/**
 * Cross-tool insight generator for Radar.
 * Analyzes results from multiple tools to find conflicts, strengths, and weaknesses.
 */

import type { ToolKey, ToolState, Insight } from '@/types/radar'
import {
  isCrawlRetrievalBot,
  type CrawlBotResult,
} from '@/lib/tools/crawl-bots'
import { hasUsableScoreData } from './tool-scores'
import { readAnswerEngineSources } from './answer-engine-result'

/* eslint-disable @typescript-eslint/no-explicit-any */

export function generateInsights(tools: Record<ToolKey, ToolState>): Insight[] {
  const insights: Insight[] = []
  const crawl = tools.crawl.data
  const citation = tools.citation.data
  const llms = tools.llms.data
  const readiness = tools.readiness.data
  const reddit = tools.reddit.data
  const robots = tools.robots.data
  const aeo = tools.aeo.data
  const answer = tools.answer.data

  // Score-threshold checks must never fire on a tool that DECLINED to grade.
  // `null < 50` is true in JS, and ai-readiness reports a withheld result as
  // 0, so both would otherwise produce confident narrative claims about a
  // score that was never measured.
  const graded = (d: any): number | null =>
    hasUsableScoreData(d) ? d.overallScore : null
  const crawlScore = graded(crawl)
  const readinessScore = graded(readiness)
  const answerScore = graded(answer)

  if (crawl && citation) {
    // Use the current retrieval pool, including for saved reports with old
    // categories. Training and general-purpose crawlers do not establish a
    // live-answer access defect.
    const blockedBrowse =
      crawl.botAccess?.filter(
        (b: CrawlBotResult) => isCrawlRetrievalBot(b.bot) && !b.allowed
      ) || []
    const blockedTrain =
      crawl.botAccess?.filter(
        (b: any) => b.category === 'ai-train' && !b.allowed
      ) || []
    const rates =
      citation.providers
        ?.filter((p: any) => p.available)
        .map((p: any) => p.overallMentionRate) || []
    const avg =
      rates.length > 0
        ? rates.reduce((a: number, b: number) => a + b, 0) / rates.length
        : 0

    // True conflict: browsing bots are blocked AND citations are low.
    // Bot names come from the actual blocked list, not a hardcoded example.
    if (blockedBrowse.length >= 1 && avg < 0.5) {
      const botNames = blockedBrowse
        .map((b: CrawlBotResult) => b.bot)
        .join(', ')
      insights.push({
        type: 'conflict',
        title: 'Blocking AI browsing bots while citation rate is low',
        message: `${blockedBrowse.length} AI browsing bot${blockedBrowse.length > 1 ? 's' : ''} blocked (${botNames}), average mention rate ${Math.round(avg * 100)}%. These bots fetch your content for live AI answers — unblocking them is the fastest path to more citations.`,
      })
    }

    // Not a conflict: training-only blocks with low citations.
    // Surface as informational so the customer knows where the gap is.
    if (blockedBrowse.length === 0 && blockedTrain.length >= 2 && avg < 0.5) {
      insights.push({
        type: 'bad',
        title: 'Low citation rate is content-driven, not robots-driven',
        message: `Browsing bots are allowed (good), but ${blockedTrain.length} training bots are blocked and citation rate is ${Math.round(avg * 100)}%. Training bot blocks are a fine policy choice — your citation gap is likely a content authority issue, not a robots.txt fix.`,
      })
    }
  }
  if (crawl && llms) {
    if (crawl.overallScore >= 70 && !llms.llmsTxtFound)
      insights.push({
        type: 'conflict',
        title: 'Good crawlability but no llms.txt',
        message:
          'Bots can access your site but lack structured context. Adding llms.txt improves how AI models describe your business.',
      })
  }
  if (crawl && readiness) {
    if (crawl.overallScore >= 80 && readiness.overallScore >= 80)
      insights.push({
        type: 'good',
        title: 'Strong technical AI foundation',
        message:
          'Bot access and AI readiness both excellent. Solid base for AI search visibility.',
      })
  }
  if (crawl?.structuredData && !crawl.structuredData.found)
    insights.push({
      type: 'bad',
      title: 'No structured data detected',
      message:
        'JSON-LD markup helps AI understand content type, authorship, relationships. High-impact fix.',
    })
  if (reddit && crawl) {
    if (
      reddit.overallScore >= 60 &&
      crawlScore !== null &&
      crawlScore < 50 &&
      crawl.botAccess?.some(
        (b: CrawlBotResult) => isCrawlRetrievalBot(b.bot) && !b.allowed
      )
    )
      insights.push({
        type: 'conflict',
        title: 'Reddit buzz but AI bots blocked',
        message:
          'Community talks about you but AI crawlers cannot access your site. Fix bot access to convert buzz into citations.',
      })
  }
  if (citation) {
    const rates =
      citation.providers
        ?.filter((p: any) => p.available)
        .map((p: any) => p.overallMentionRate) || []
    const avg =
      rates.length > 0
        ? rates.reduce((a: number, b: number) => a + b, 0) / rates.length
        : 0
    if (avg >= 0.7)
      insights.push({
        type: 'good',
        title: 'Strong AI citation presence',
        message: `Mentioned in ${Math.round(avg * 100)}% of test queries. Already visible in AI answers.`,
      })

    // Enhanced sentiment: detect cautionary framing across providers
    const allQueries =
      citation.providers
        ?.filter((p: any) => p.available)
        .flatMap((p: any) => p.queries || []) || []
    const cautionaryCount = allQueries.filter(
      (q: any) => q.sentimentDetail?.tone === 'cautionary'
    ).length
    const mentionedCount = allQueries.filter(
      (q: any) => q.brandMentioned
    ).length
    if (mentionedCount > 0 && cautionaryCount / mentionedCount >= 0.3)
      insights.push({
        type: 'bad',
        title: 'AI platforms frame your brand cautiously',
        message: `${Math.round((cautionaryCount / mentionedCount) * 100)}% of mentions include caveats or warnings. Review the sentiment details to understand what concerns AI models are surfacing.`,
      })
  }
  if (citation && crawl && readiness) {
    const rates =
      citation.providers
        ?.filter((p: any) => p.available)
        .map((p: any) => p.overallMentionRate) || []
    const avg =
      rates.length > 0
        ? rates.reduce((a: number, b: number) => a + b, 0) / rates.length
        : 0
    if (avg < 0.3 && crawl.overallScore >= 70 && readiness.overallScore >= 60)
      insights.push({
        type: 'bad',
        title: 'Technically visible but not cited',
        message:
          'Site is accessible to AI but not being mentioned. Focus on content authority and topical depth.',
      })
  }
  if (
    robots &&
    crawl &&
    robots.overallScore >= 70 &&
    crawlScore !== null &&
    crawlScore < 50
  )
    insights.push({
      type: 'conflict',
      title: 'Robots.txt fine but crawl access poor',
      message:
        'Rules look reasonable but actual access failing. Check CDN/WAF blocks, meta robots, or x-robots-tag headers.',
    })
  if (llms?.llmsTxtFound && llms.overallScore < 50)
    insights.push({
      type: 'conflict',
      title: 'llms.txt needs improvement',
      message: `Scored ${llms.overallScore}/100. Improve structure, entity definitions, and link completeness.`,
    })
  if (reddit?.seedingMatrix) {
    const seeded =
      reddit.seedingMatrix.likelySeeded + reddit.seedingMatrix.suspicious
    if (
      seeded > 0 &&
      reddit.totalMentions > 0 &&
      Math.round((seeded / reddit.totalMentions) * 100) >= 30
    )
      insights.push({
        type: 'bad',
        title: 'Suspicious Reddit activity',
        message: `${Math.round((seeded / reddit.totalMentions) * 100)}% of mentions show seeding signals. Could hurt brand trust.`,
      })
  }

  // Single-tool insights
  if (crawl) {
    const retrievalBots =
      crawl.botAccess?.filter((b: CrawlBotResult) =>
        isCrawlRetrievalBot(b.bot)
      ) || []
    const allowed = retrievalBots.filter(
      (b: CrawlBotResult) => b.allowed
    ).length
    const total = retrievalBots.length
    if (total > 0 && allowed === total)
      insights.push({
        type: 'good',
        title: 'All AI retrieval bots can access your site',
        message: `${allowed}/${total} AI retrieval bots passed access checks. No retrieval crawl blockers detected.`,
      })
    else if (total > 0 && allowed < total * 0.5)
      insights.push({
        type: 'bad',
        title: 'More than half of AI retrieval bots are blocked',
        message: `Only ${allowed}/${total} AI retrieval bots can access your site. This limits retrieval access for AI answers.`,
      })
  }
  if (readiness && readinessScore !== null && readinessScore < 40)
    insights.push({
      type: 'bad',
      title: 'Low AI readiness',
      message: `Readiness score is ${readiness.overallScore}/100. Multiple areas need attention before AI search engines can properly index your content.`,
    })
  if (robots && robots.overallScore >= 80)
    insights.push({
      type: 'good',
      title: 'Well-configured robots.txt',
      message: `Scored ${robots.overallScore}/100. Your robots.txt follows best practices for AI and search bot management.`,
    })
  if (llms && !llms.llmsTxtFound)
    insights.push({
      type: 'bad',
      title: 'No llms.txt file',
      message:
        'Your site has no llms.txt file. This emerging standard helps AI models understand your business context, products, and preferred citation format.',
    })
  if (llms && llms.llmsTxtFound && llms.overallScore >= 70)
    insights.push({
      type: 'good',
      title: 'Strong llms.txt',
      message: `Your llms.txt scored ${llms.overallScore}/100. AI models have good context about your business.`,
    })
  if (reddit && reddit.totalMentions > 5)
    insights.push({
      type: 'good',
      title: 'Active Reddit presence',
      message: `${reddit.totalMentions} mentions found. Reddit is a key training data source for AI models, and community discussion signals authority.`,
    })
  if (reddit && reddit.totalMentions === 0)
    insights.push({
      type: 'bad',
      title: 'No Reddit presence',
      message:
        'Zero Reddit mentions detected. Building organic community presence on Reddit can influence how AI models perceive and recommend your brand.',
    })

  // AEO cross-tool insights
  if (aeo && citation) {
    const rates =
      citation.providers
        ?.filter((p: any) => p.available)
        .map((p: any) => p.overallMentionRate) || []
    const avg =
      rates.length > 0
        ? rates.reduce((a: number, b: number) => a + b, 0) / rates.length
        : 0
    if (aeo.overallScore >= 70 && avg < 0.3)
      insights.push({
        type: 'conflict',
        title: 'Well-formatted content but not cited',
        message: `AEO score is ${aeo.overallScore}/100 but citation rate is only ${Math.round(avg * 100)}%. Content is extractable but lacks topical authority or backlinks.`,
      })
  }
  if (aeo && crawl) {
    if (aeo.overallScore < 40 && crawl.overallScore >= 70)
      insights.push({
        type: 'conflict',
        title: 'Accessible but not AEO-optimized',
        message:
          'AI bots can crawl your site but content is not formatted for answer engine extraction. Add speakable schema, answer-first structure, and structured data callouts.',
      })
  }
  if (aeo) {
    if (aeo.overallScore >= 80)
      insights.push({
        type: 'good',
        title: 'Strong AEO page formatting',
        message: `AEO audit scored ${aeo.overallScore}/100. Content is well-structured for AI extraction and citation.`,
      })
    else if (aeo.overallScore < 40)
      insights.push({
        type: 'bad',
        title: 'Poor AEO readiness',
        message: `AEO score is ${aeo.overallScore}/100. Missing speakable schema, answer-first content structure, or structured data elements that AI engines need to cite your page.`,
      })
  }

  // Answer Engine cross-tool insights
  if (answerScore !== null && aeo) {
    if (answerScore >= 70 && aeo.overallScore < 40)
      insights.push({
        type: 'conflict',
        title: 'Cited by AI but poorly formatted for extraction',
        message: `Citation test scored ${answerScore}/100 but AEO is ${aeo.overallScore}/100. You are getting cited now, but better AEO formatting would increase citation frequency and accuracy.`,
      })
    if (answerScore < 30 && aeo.overallScore >= 70)
      insights.push({
        type: 'conflict',
        title: 'Well-formatted but not cited',
        message: `AEO score is ${aeo.overallScore}/100 but citation test is ${answerScore}/100. Content structure is good but lacks topical authority or domain reputation for this query.`,
      })
  }
  if (answerScore !== null) {
    if (answerScore >= 70)
      insights.push({
        type: 'good',
        title: 'AI platforms are citing your page',
        message: `Citation test scored ${answerScore}/100. Your page is being surfaced in AI answers for the tested question.`,
      })
    else if (answerScore < 30)
      insights.push({
        type: 'bad',
        title: 'Not cited by AI platforms',
        message: `Citation test scored ${answerScore}/100. AI platforms are not citing your page for the tested question. Competitors may be getting cited instead.`,
      })
  }
  // Verified competitors only. An unverified non-target citation is another
  // source (a directory, marketplace, publication or social profile), and
  // calling it a competitor was a fabricated finding.
  const answerSources = answer ? readAnswerEngineSources(answer) : null
  if (answerSources && answerSources.verifiedCompetitorUrls.length > 0)
    insights.push({
      type: 'bad',
      title: `${answerSources.verifiedCompetitorUrls.length} verified competitor(s) cited instead`,
      message: `Perplexity cited ${answerSources.verifiedCompetitorUrls.length} verified competitor URL(s) for your target question. Study their content structure to identify gaps.`,
    })

  // ── New tool insights (Phase 2+3) ──────────────────────────────────────────

  const sourceInfluence = tools.sourceInfluence?.data
  const promptSovData = tools.promptSov?.data
  const promptSov = graded(promptSovData) !== null ? promptSovData : null
  const schemaAudit = tools.schemaAudit?.data
  const hallucinationCheck = tools.hallucinationCheck?.data

  // SOV vs Citation conflicts
  if (promptSov && citation) {
    const citRates =
      citation.providers
        ?.filter((p: any) => p.available)
        .map((p: any) => p.overallMentionRate) || []
    const citAvg =
      citRates.length > 0
        ? citRates.reduce((a: number, b: number) => a + b, 0) / citRates.length
        : 0
    if (promptSov.targetSOV?.sovPercent >= 15 && citAvg < 0.3)
      insights.push({
        type: 'conflict',
        title: 'Visible in SOV but low citation rate',
        message: `${promptSov.targetSOV.sovPercent}% share of voice but only ${Math.round(citAvg * 100)}% citation rate. AI models mention you in recommendations but not in direct queries. Focus on brand-specific content authority.`,
      })
    if ((promptSov.targetSOV?.mentionCount || 0) === 0 && citAvg >= 0.5)
      insights.push({
        type: 'conflict',
        title: 'Cited but not ranking in category',
        message:
          'AI cites your brand in direct queries but does not include you in category recommendations. Create definitive content for your category keywords.',
      })
  }

  // SOV standalone
  if (promptSov) {
    const hasTarget = (promptSov.targetSOV?.mentionCount || 0) > 0
    if (hasTarget && promptSov.targetSOV?.rank === 1)
      insights.push({
        type: 'good',
        title: '#1 in AI share of voice',
        message: `Your brand leads the category with ${promptSov.targetSOV.sovPercent}% SOV across ${promptSov.targetSOV.providers?.length || 0} provider(s).`,
      })
    else if (!hasTarget)
      insights.push({
        type: 'bad',
        title: 'Not appearing in category recommendations',
        message: `AI providers do not mention your brand when asked about your category. ${promptSov.totalBrandsFound || 0} other brands were found.`,
      })
  }

  // Source Influence insights
  if (sourceInfluence) {
    if (
      !sourceInfluence.brandInSources &&
      sourceInfluence.topSources?.length > 0
    )
      insights.push({
        type: 'bad',
        title: 'Your domain is not a cited source',
        message: `AI models cite ${sourceInfluence.topSources.length} sources for your category but your domain is not among them. Top source: ${sourceInfluence.topSources[0]?.domain}.`,
      })
    if (sourceInfluence.brandInSources)
      insights.push({
        type: 'good',
        title: 'Your domain is cited as a category source',
        message:
          'AI providers reference your website when discussing your category. Maintain content freshness to keep this position.',
      })
  }

  // Schema Audit vs AEO
  if (schemaAudit && aeo) {
    if (schemaAudit.overallScore < 40 && aeo.overallScore >= 60)
      insights.push({
        type: 'conflict',
        title: 'Good AEO content but weak structured data',
        message: `AEO score is ${aeo.overallScore}/100 but schema audit is ${schemaAudit.overallScore}/100. Your content is well-structured but missing JSON-LD markup that AI engines use for validation.`,
      })
  }
  if (schemaAudit) {
    if (!schemaAudit.hasSpeakable)
      insights.push({
        type: 'bad',
        title: 'No SpeakableSpecification detected',
        message:
          'Your pages lack speakable schema. AI assistants and voice search cannot identify which content to read aloud or cite.',
      })
    if (schemaAudit.overallScore >= 80)
      insights.push({
        type: 'good',
        title: 'Strong structured data coverage',
        message: `Schema audit scored ${schemaAudit.overallScore}/100. Your JSON-LD markup gives AI models clear signals about your content.`,
      })
  }

  // Hallucination insights
  if (hallucinationCheck) {
    const gradedHallucinationCheck =
      hallucinationCheck.scoreStatus !== 'withheld' &&
      typeof hallucinationCheck.overallScore === 'number'
    if (
      gradedHallucinationCheck &&
      Array.isArray(hallucinationCheck.judgedProviders) &&
      hallucinationCheck.totalInaccuracies === 0
    )
      insights.push({
        type: 'good',
        title: 'No supported factual inaccuracies detected',
        message: `No supported factual inaccuracies detected across ${hallucinationCheck.judgedProviders.length} judged AI providers.`,
      })
    if (gradedHallucinationCheck && hallucinationCheck.bySeverity?.high > 0)
      insights.push({
        type: 'bad',
        title: `${hallucinationCheck.bySeverity.high} high-severity hallucination(s)`,
        message: `AI models are stating incorrect core facts about your brand. Update llms.txt and structured data to provide authoritative corrections.`,
      })
  }

  // Hallucination vs llms.txt
  if (hallucinationCheck && llms) {
    if (
      hallucinationCheck.scoreStatus !== 'withheld' &&
      hallucinationCheck.totalInaccuracies > 0 &&
      !llms.llmsTxtFound
    )
      insights.push({
        type: 'conflict',
        title: 'Hallucinations detected but no llms.txt to correct them',
        message:
          'AI models have wrong facts about your brand and no llms.txt exists to provide corrections. Creating llms.txt is the fastest way to address this.',
      })
  }

  return insights
}

/* eslint-enable @typescript-eslint/no-explicit-any */
